import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Grid,
    Paper,
    Stack,
    IconButton,
    Tooltip,
    alpha
} from '@mui/material';
import {
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { formatCurrency, formatDate, parseAnyDate } from '../../../utils/compteClientEngine';
import { COLORS, jsonToXlsx } from '../../../settings/themeSettings';

const HistoriqueEncaissementsModal = ({ open, onClose, encaissements = [], onSelectRecu }) => {
    // Filtres de recherche
    const [clientFilter, setClientFilter] = useState('');
    const [factureFilter, setFactureFilter] = useState('');
    const [recuFilter, setRecuFilter] = useState('');
    const [montantFilter, setMontantFilter] = useState('');
    const [dateDebut, setDateDebut] = useState('');
    const [dateFin, setDateFin] = useState('');

    const filteredList = useMemo(() => {
        return encaissements.filter(item => {
            if (clientFilter) {
                const cName = item.clientName || item.client?.libelle || item.client?.nom || '';
                if (!cName.toLowerCase().includes(clientFilter.toLowerCase())) return false;
            }

            if (recuFilter) {
                const rNum = item.numeroRecu || `E-${item.id}`;
                if (!rNum.toLowerCase().includes(recuFilter.toLowerCase())) return false;
            }

            if (factureFilter) {
                const facts = (item.lignes || []).map(l => l.numeroFacture || `F-${l.factureId}`).join(' ');
                if (!facts.toLowerCase().includes(factureFilter.toLowerCase())) return false;
            }

            if (montantFilter) {
                const mTarget = parseFloat(montantFilter);
                if (!isNaN(mTarget)) {
                    const diff = Math.abs(Number(item.montantEncaisse || item.montant || 0) - mTarget);
                    if (diff > 500) return false; // Recherche approchée à 500 CFA près
                }
            }

            if (dateDebut || dateFin) {
                const eDate = parseAnyDate(item.dateEncaissement || item.date || item.createdAt);
                if (eDate && eDate.isValid()) {
                    if (dateDebut && eDate.isBefore(parseAnyDate(dateDebut).startOf('day'))) return false;
                    if (dateFin && eDate.isAfter(parseAnyDate(dateFin).endOf('day'))) return false;
                }
            }

            return true;
        });
    }, [encaissements, clientFilter, factureFilter, recuFilter, montantFilter, dateDebut, dateFin]);

    const handleExportExcel = () => {
        const data = filteredList.map(e => ({
            'N° Reçu': e.numeroRecu || `E-${e.id}`,
            'Date': formatDate(e.dateEncaissement || e.date),
            'Client': e.clientName || e.client?.libelle || e.client?.nom || '-',
            'Mode de paiement': e.modePaiement || e.methode || '-',
            'Référence': e.reference || '-',
            'Montant encaissé (CFA)': Number(e.montantEncaisse || e.montant || 0),
            'Factures réglées': (e.lignes || []).map(l => l.numeroFacture || `F-${l.factureId}`).join(', ')
        }));
        jsonToXlsx(data, "Historique_Encaissements");
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#0f172a', color: 'white', py: 1.5 }}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">
                        Historique des encaissements
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Consultation et réimpression des reçus de règlements clients
                    </Typography>
                </Box>
                <Button size="small" onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
                <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: '8px', border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: '#0f172a', textTransform: 'uppercase' }}>
                        Rechercher un encaissement
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2.5}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Nom client"
                                placeholder="Recherche dynamique..."
                                value={clientFilter}
                                onChange={(e) => setClientFilter(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="N° Facture"
                                placeholder="Recherche directe..."
                                value={factureFilter}
                                onChange={(e) => setFactureFilter(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="N° Reçu"
                                placeholder="Recherche directe..."
                                value={recuFilter}
                                onChange={(e) => setRecuFilter(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Montant"
                                placeholder="Exact ou approché..."
                                value={montantFilter}
                                onChange={(e) => setMontantFilter(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1.75}>
                            <TextField
                                type="date"
                                fullWidth
                                size="small"
                                label="De"
                                InputLabelProps={{ shrink: true }}
                                value={dateDebut}
                                onChange={(e) => setDateDebut(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={1.75}>
                            <TextField
                                type="date"
                                fullWidth
                                size="small"
                                label="À"
                                InputLabelProps={{ shrink: true }}
                                value={dateFin}
                                onChange={(e) => setDateFin(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* TABLEAU DES RÉSULTATS */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {filteredList.length} encaissement{filteredList.length > 1 ? 's' : ''} enregistré{filteredList.length > 1 ? 's' : ''}
                    </Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export Excel
                    </Button>
                </Stack>

                <Paper elevation={0} sx={{ border: '1px solid #0f172a', borderRadius: '6px', overflow: 'hidden', bgcolor: 'white' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '140px 110px 1.5fr 140px 150px 1.5fr 80px', bgcolor: '#0f172a', color: 'white', p: 1.2, fontWeight: 'bold', fontSize: '0.85rem' }}>
                        <Box>N° Reçu</Box>
                        <Box>Date</Box>
                        <Box>Client</Box>
                        <Box>Mode paiement</Box>
                        <Box sx={{ textAlign: 'right' }}>Montant encaissé</Box>
                        <Box sx={{ pl: 2 }}>Factures réglées</Box>
                        <Box sx={{ textAlign: 'center' }}>Action</Box>
                    </Box>

                    <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                        {filteredList.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                Aucun encaissement ne correspond aux filtres.
                            </Box>
                        ) : (
                            filteredList.map((enc, idx) => {
                                const recuNum = enc.numeroRecu || `E-${enc.id || idx + 1}`;
                                const dateStr = formatDate(enc.dateEncaissement || enc.date);
                                const clientName = enc.clientName || enc.client?.libelle || enc.client?.nom || '-';
                                const mode = enc.modePaiement || enc.methode || '-';
                                const refStr = enc.reference ? ` (${enc.reference})` : '';
                                const montant = Number(enc.montantEncaisse || enc.montant || 0);
                                const facturesReglees = (enc.lignes || []).map(l => l.numeroFacture || `F-${l.factureId}`).join(', ') || '-';

                                return (
                                    <Box
                                        key={enc.id || idx}
                                        onClick={() => onSelectRecu(enc)}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '140px 110px 1.5fr 140px 150px 1.5fr 80px',
                                            p: 1.2,
                                            alignItems: 'center',
                                            fontSize: '0.85rem',
                                            borderBottom: '1px solid #e2e8f0',
                                            bgcolor: idx % 2 === 0 ? 'white' : '#f8fafc',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                bgcolor: alpha(COLORS.primary, 0.08)
                                            }
                                        }}
                                    >
                                        <Box sx={{ fontWeight: 700, color: COLORS.primary }}>{recuNum}</Box>
                                        <Box sx={{ color: 'text.secondary' }}>{dateStr}</Box>
                                        <Box sx={{ fontWeight: 600 }}>{clientName}</Box>
                                        <Box sx={{ color: 'text.secondary' }}>{mode}{refStr}</Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>
                                            {formatCurrency(montant)} CFA
                                        </Box>
                                        <Box sx={{ pl: 2, color: 'text.secondary', fontSize: '0.8rem' }}>
                                            {facturesReglees}
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Tooltip title="Voir et imprimer le reçu">
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onSelectRecu(enc); }}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Paper>
            </DialogContent>

            <DialogActions sx={{ p: 2, bgcolor: '#f1f5f9' }}>
                <Button variant="outlined" onClick={onClose} sx={{ textTransform: 'none' }}>
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HistoriqueEncaissementsModal;
